import { State, process } from '@progress/kendo-data-query';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckbookCategoryOption,
  EditableColumnCategories,
  initialEditableYearColumns,
  viewCheckbookCategories,
} from 'src/assets/static/view_checkbook/useCheckbookCategories';
import useCheckbookYearColumns from 'src/assets/static/view_checkbook/useCheckbookYearColumns';
import useAuth from 'src/hooks/useAuth';
import useLocalStorage from 'src/hooks/useLocalstorage';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodesBudgetByYearResponseObject } from 'src/services/useGlCodesBudgetByYearApi';
import { IFilters } from 'src/types/PostBody';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { YEAR_VIEW_DATE_FIELD_ARRAY, YEAR_VIEW_YEAR_FIELD_ARRAY } from './ViewYearCheckbookTable';
import { viewCheckbookFilters } from './viewCheckbookFilters';

interface YearViewContextType {
  yearViewDropdownFilter: CheckbookCategoryOption;
  setYearViewDropdownFilter: SetState<CheckbookCategoryOption>;
  yearViewData: GLCodesBudgetByYearResponseObject[];
  yearViewTotal: number;
  getYearFilters: (state: State) => IFilters[];
  getYearSorts: (state: State) => any[];
  editableColumnDropdownData: EditableColumnCategories[];
  setEditableColumnDropdownData: (value: EditableColumnCategories[]) => void;
}

export const YearViewContext = createContext<YearViewContextType>({} as YearViewContextType);

interface YearViewContextProps {
  children: ReactNode;
}

const YEAR_VIEW_DROPDOWN_FILTER_INITIAL_STATE = viewCheckbookCategories[2];

const YearViewContextProvider: FC<YearViewContextProps> = ({ children }) => {
  const [yearViewDropdownFilter, setYearViewDropdownFilter] = useState<CheckbookCategoryOption>(
    YEAR_VIEW_DROPDOWN_FILTER_INITIAL_STATE
  );
  const { user } = useAuth();
  const {
    glCodesBudgetByYear,
    yearCheckbookDataState,
    isEditMode,
    isPeriodView,
    isYearTableDirty,
    setEditingGlCodeYearBudget,
    setOriginalGlCodeYearBudget,
    dropDownYearValue,
  } = useViewCheckbook();
  const viewYearCheckbookColumns = useCheckbookYearColumns(
    isEditMode,
    false,
    dropDownYearValue?.periods?.length ?? 12
  );

  const [editableColumnDropdownData, setEditableColumnDropdownData] = useLocalStorage(
    `yearView-columns-${user.ClientId}`,
    initialEditableYearColumns
  );

  const editableYearColumns = useMemo(() => {
    const editableFields = initialEditableYearColumns.map((c) => c.field);
    let editableYearColumns: string[] = [];
    viewYearCheckbookColumns.forEach((item) => {
      if (item.field?.includes('period')) {
        if (
          !editableYearColumns.includes('initialBudgetPeriod') ||
          !editableYearColumns.includes('budgetPeriod')
        ) {
          item?.childrenColumn?.forEach((c) => {
            editableYearColumns.push(c.field?.slice(0, -2) as string);
          });
        }
      }
      if (item.field?.includes('glcode_type')) {
        if (!editableYearColumns.includes('isFixed') || !editableYearColumns.includes('isActive')) {
          item?.childrenColumn?.forEach((c) => {
            if (editableFields.includes(c.field || '')) {
              editableYearColumns.push(c.field as string);
            }
          });
        }
      }
      if (item.field === 'general_ledger') {
        item?.childrenColumn?.forEach((c) => {
          if (editableFields.includes(c.field || '')) {
            editableYearColumns.push(c.field as string);
          }
        });
      }
      if (editableFields.includes(item.field || '')) {
        editableYearColumns.push(item.field as string);
      }
    });
    return editableYearColumns;
  }, [viewYearCheckbookColumns]);

  useEffect(() => {
    setEditableColumnDropdownData((prev) =>
      prev.map((item) => {
        const findColumn = editableYearColumns.find((field) => field === item.field);
        return { ...item, display: findColumn ? true : false };
      })
    );
  }, [editableYearColumns, setEditableColumnDropdownData]);

  const getYearFilters = useCallback(
    (state: State) => {
      const data = {
        ...modifyRequest(
          state,
          YEAR_VIEW_DATE_FIELD_ARRAY,
          undefined,
          YEAR_VIEW_YEAR_FIELD_ARRAY,
          false
        ),
      };
      data.filters = [
        ...data.filters,
        ...(viewCheckbookFilters[yearViewDropdownFilter.value]?.filters || []),
      ];
      return data.filters;
    },
    [yearViewDropdownFilter.value]
  );
  const getYearSorts = useCallback((state: State) => {
    const data = {
      ...modifyRequest(
        state,
        YEAR_VIEW_DATE_FIELD_ARRAY,
        undefined,
        YEAR_VIEW_YEAR_FIELD_ARRAY,
        false
      ),
    };

    return data.sort;
  }, []);

  const { data: yearViewData, total: yearViewTotal } = useMemo(
    () =>
      process(glCodesBudgetByYear, {
        ...yearCheckbookDataState,
        filter: {
          logic: 'and',
          filters: getYearFilters(yearCheckbookDataState),
        },
        sort: getYearSorts(yearCheckbookDataState),
      }) as {
        data: any[];
        total: number;
      },
    [glCodesBudgetByYear, yearCheckbookDataState, getYearFilters, getYearSorts]
  );

  useEffect(() => {
    if (isEditMode && !isPeriodView && !isYearTableDirty) {
      setEditingGlCodeYearBudget((prev) => {
        const newlyAddedLineItems = [...prev].filter((item) => item.isNewGlCode);
        const allItems = [...newlyAddedLineItems, ...yearViewData];
        setOriginalGlCodeYearBudget(allItems);
        return allItems;
      });
    }
  }, [
    isEditMode,
    isPeriodView,
    isYearTableDirty,
    setEditingGlCodeYearBudget,
    setOriginalGlCodeYearBudget,
    yearViewData,
  ]);

  return (
    <YearViewContext.Provider
      value={{
        yearViewDropdownFilter,
        setYearViewDropdownFilter,
        yearViewData,
        yearViewTotal,
        getYearFilters,
        getYearSorts,
        editableColumnDropdownData,
        setEditableColumnDropdownData,
      }}
    >
      {children}
    </YearViewContext.Provider>
  );
};

export default YearViewContextProvider;
